import { defineStore } from 'pinia'
import { ref } from 'vue'
import Network from '@/Network'
import type { DemandTimelineRequest } from '@/Network/Types/Requests/DemandRequests'
import type { DemandTimelineResponse } from '@/Network/Types/Responses/DemandResponses'

/**
 * Timeline por setor (Onda 2.3, spec §5.3): `GET /demand/timeline` de um dia. Padrão do
 * `useMatrixSummaryStore`: `fetchId` descarta respostas atrasadas (troca rápida de dia) e o
 * mesmo pedido em voo é reusado (dedupe por chave JSON).
 */
export const useDemandTimelineStore = defineStore('demandTimeline', () => {
  const data = ref<DemandTimelineResponse | null>(null)
  const error = ref<unknown | null>(null)
  /** Carga normal (esqueleto). */
  const isLoading = ref(false)
  /** Recarga silenciosa (polling): sem esqueleto, a tela segue com os dados à vista. */
  const isRefreshing = ref(false)
  /**
   * Última unidade escolhida/mostrada na Timeline nesta sessão (só memória, sem localStorage):
   * padrão ao voltar para a aba quando a URL perdeu `unidade`. O `$reset` do dashboard não a
   * apaga — é preferência da sessão, não dado carregado.
   */
  const lastUnitId = ref<number | null>(null)
  let fetchId = 0
  let inFlight: { key: string; promise: Promise<void> } | null = null

  function fetch(req: DemandTimelineRequest, opts: { silent?: boolean } = {}): Promise<void> {
    const key = JSON.stringify(req)
    if (inFlight?.key === key) return inFlight.promise
    const promise = request(req, opts.silent === true).finally(() => {
      if (inFlight?.promise === promise) inFlight = null
    })
    inFlight = { key, promise }
    return promise
  }

  async function request(req: DemandTimelineRequest, silent: boolean): Promise<void> {
    const id = ++fetchId
    if (silent) isRefreshing.value = true
    else {
      isLoading.value = true
      error.value = null
    }
    try {
      const response = await Network.demand.timeline(req)
      if (id === fetchId) {
        data.value = response
        error.value = null
      }
    } catch (e) {
      // Sem `console.error`/handler global: o 404 `DEMAND_HEALTH_UNIT_NOT_FOUND` é esperado
      // (unidade sem escala visível) e vira o estado "Sem escalas visíveis nesta unidade".
      // Recarga silenciosa que falha mantém o dia à vista; a carga normal mostra o erro
      // com "Tentar de novo", nunca uma timeline vazia.
      if (id === fetchId && !silent) {
        data.value = null
        error.value = e
      }
    } finally {
      // Só o pedido mais recente desliga os indicadores (um silencioso que atropela uma
      // carga normal também encerra o esqueleto dela).
      if (id === fetchId) {
        isLoading.value = false
        isRefreshing.value = false
      }
    }
  }

  function rememberUnit(id: number): void {
    lastUnitId.value = id
  }

  function $reset(): void {
    inFlight = null
    fetchId++
    data.value = null
    error.value = null
    isLoading.value = false
    isRefreshing.value = false
  }

  return { data, error, isLoading, isRefreshing, lastUnitId, fetch, rememberUnit, $reset }
})
